import { useFormContext } from 'react-hook-form'
import { IntegrationSettings } from '../../../schema'
import { Col, Row } from 'react-bootstrap'
import { SelectDropdown, SelectOption } from '../../../../../../../components/SelectDropdown/SelectDropdown'
import { getBusinessEventLabel, getIntegrationDisplayName, IntegrationServiceId } from '../../../../../../../common/integrations'
import { MappingType, OperationType } from '../../../../../../../models/Account'
import { useGetSupportedObjectsQuery } from '../../../../../../../store/integrations/integrations'
import { useMemo } from 'react'
import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../../../store/hooks'
import { useGetIntegrationAdminSettings } from '../../../hooks'
import { useGetSchemaErrors } from '../../../schemaErrors'
import { BusinessEventType } from '../../../../../../../models/BusinessEvent'

interface SelectBusinessEventTypeSectionProps {
  onChange?: () => void
  serviceId: IntegrationServiceId
  businessEventType: BusinessEventType
  disabled?: boolean
}

export const SelectBusinessEventTypeSection: React.FC<SelectBusinessEventTypeSectionProps> = ({ onChange, serviceId, businessEventType, disabled }) => {
  const { watch, setValue } = useFormContext<IntegrationSettings>()
  const accountId = useAppSelector(selectAccountId)

  const mappingPath = `mappings.businessEvents.${businessEventType}` as const

  const currentObjectName = watch(`${mappingPath}.objectName` as const)

  const businessEventCRMTypeOptions: SelectOption[] = [{ id: 'custom', label: 'Custom' }]

  const { hasIntegration } = useGetIntegrationAdminSettings(serviceId)

  const { data: supportedObjects, isLoading: isLoadingSupportedObjects } = useGetSupportedObjectsQuery(
    { accountId, connector: serviceId },
    { skip: !accountId || !serviceId || !hasIntegration },
  )

  const customBusinessEventOption = useMemo(
    () => supportedObjects?.find((opt) => opt.name === currentObjectName) || null,
    [supportedObjects, currentObjectName],
  )

  const customBusinessEventOptions: SelectOption[] = useMemo(() => {
    return supportedObjects?.map((obj) => ({ id: obj.name, label: `${obj.label} (${obj.name})` })) || []
  }, [supportedObjects])

  const handleCustomOptionSelect = (option: SelectOption) => {
    if (option.id === currentObjectName) {
      return
    }

    const optionLabel = supportedObjects?.find((obj) => obj.name === option.id)?.label || null

    setValue(
      mappingPath,
      {
        objectName: option.id,
        displayName: optionLabel,
        operationType: OperationType.CREATE,
        mappingType: MappingType.CUSTOM,
        fields: {},
        additionalInformation: {},
        contactLinks: [],
        constraints: {},
      },
      { shouldDirty: true, shouldValidate: true },
    )
    onChange?.()
  }

  const { getErrorOnPath } = useGetSchemaErrors()

  const fieldError = getErrorOnPath(`${mappingPath}.objectName`)
  const objectError = getErrorOnPath(mappingPath)

  const integrationDisplayName = useMemo(() => getIntegrationDisplayName(serviceId), [serviceId])

  const initialSelectedOptions = customBusinessEventOption
    ? {
        id: customBusinessEventOption.name,
        label: `${customBusinessEventOption.label} (${customBusinessEventOption.name})`,
      }
    : null

  return (
    <>
      <Row className='mb-2'>
        <Col>
          <h4>Select Object Type</h4>
          <p className='zocks-text-gray-600'>Select the {integrationDisplayName} object where Client Signals will be saved.</p>
        </Col>
      </Row>

      <Row>
        <Col md={6} className='d-flex flex-column align-items-start gap-1'>
          <div className='paragraph-small zocks-text-gray-900'>
            '{getBusinessEventLabel(businessEventType)}' saves to this {integrationDisplayName} object
          </div>
          <SelectDropdown initialSelectedOption={businessEventCRMTypeOptions[0]} options={businessEventCRMTypeOptions} disabled={true} />
          {fieldError && <div className='text-danger mt-1 small'>{fieldError}</div>}
          {objectError && <div className='text-danger mt-1 small'>{objectError}</div>}
        </Col>

        <Col md={6} className='d-flex flex-column align-items-start gap-1'>
          <div className='paragraph-small zocks-text-gray-900'>Select custom object type</div>
          <SelectDropdown
            options={customBusinessEventOptions}
            initialSelectedOption={initialSelectedOptions}
            placeholder='Select Object Type'
            onOptionSelected={handleCustomOptionSelect}
            searchable
            loading={isLoadingSupportedObjects}
            disabled={disabled}
          />
        </Col>
      </Row>
    </>
  )
}
